/**
 * Static presentation metadata for evaluation criteria.
 *
 * Group assignment lives on the row (evaluation_criteria.group_key, added in
 * migration 0046). This file holds the UI-only attributes — colour, label,
 * order — keyed by the group. The CriterionGroup type is owned by
 * database.types.ts (mirroring the CHECK constraint) and re-exported here so
 * UI imports stay short.
 *
 * The legacy slug→group fallback (CRITERION_GROUP_BY_SLUG / groupForCriterion)
 * is retained for callers that only have a slug at hand. Prefer reading
 * criterion.group_key directly when the row is available.
 */

import type { CriterionGroup } from "@/infrastructure/supabase/database.types";

export type { CriterionGroup };

export const CRITERION_GROUP_LABEL: Record<CriterionGroup, string> = {
  strategic: "Strategic",
  coverage: "Coverage",
  extensibility: "Extensibility",
  structural: "Structural",
  israeli: "Israeli context",
  execution: "Execution",
};

export const CRITERION_GROUP_ORDER: readonly CriterionGroup[] = [
  "strategic",
  "coverage",
  "extensibility",
  "structural",
  "israeli",
  "execution",
] as const;

/** OKLCH ramp anchored on the mission-control cyan accent; each group gets a distinct hue. */
export const CRITERION_GROUP_COLOR: Record<CriterionGroup, string> = {
  strategic: "oklch(0.55 0.17 190)", // cyan — engagement-load-bearing
  coverage: "oklch(0.58 0.16 145)", // green — does it cover the work?
  extensibility: "oklch(0.55 0.16 260)", // indigo — long-run flexibility
  structural: "oklch(0.62 0.16 50)", // orange — architectural risk
  israeli: "oklch(0.58 0.17 350)", // magenta — local context
  execution: "oklch(0.62 0.14 90)", // gold — humans and ops
};

export const CRITERION_GROUP_COLOR_SOFT: Record<CriterionGroup, string> = {
  strategic: "oklch(0.92 0.07 190)",
  coverage: "oklch(0.92 0.07 145)",
  extensibility: "oklch(0.92 0.07 260)",
  structural: "oklch(0.92 0.07 50)",
  israeli: "oklch(0.92 0.07 350)",
  execution: "oklch(0.92 0.07 90)",
};

/** Maps each seeded criterion slug to its presentation group. */
export const CRITERION_GROUP_BY_SLUG: Record<string, CriterionGroup> = {
  "yandex-api-reuse-leverage": "strategic",
  "time-to-mvp": "strategic",
  "tco-at-scale": "strategic",

  "b2c-feature-coverage": "coverage",
  "non-mvp-b2b-feature-coverage": "coverage",
  "all-in-one-coverage": "coverage",

  "extensibility-headroom": "extensibility",
  "market-credibility-maturity": "extensibility",

  "multi-tenant-isolation": "structural",
  "data-ownership-exit-cost": "structural",
  "identity-federation": "structural",

  "israeli-market-fit": "israeli",
  "israeli-compliance-posture": "israeli",

  "admin-ux-merchants": "execution",
  "roadmap-executability": "execution",
  "il-ecosystem-marketplace": "execution",
};

export function groupForCriterion(slug: string): CriterionGroup {
  return CRITERION_GROUP_BY_SLUG[slug] ?? "execution";
}

export function colorForCriterion(slug: string): string {
  return CRITERION_GROUP_COLOR[groupForCriterion(slug)];
}

export function softColorForCriterion(slug: string): string {
  return CRITERION_GROUP_COLOR_SOFT[groupForCriterion(slug)];
}

export function colorForGroup(group: CriterionGroup): string {
  return CRITERION_GROUP_COLOR[group];
}

export function softColorForGroup(group: CriterionGroup): string {
  return CRITERION_GROUP_COLOR_SOFT[group];
}
